import * as styles from "./MapMarker.css";

import { clsx } from "clsx";
import { type JSX, useMemo, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { resolveSize } from "@frontend/styles";
import { useLingui } from "@lingui/react/macro";
import { AdvancedMarker, useMap } from "@vis.gl/react-google-maps";

import type { MapMarkerProps } from "./MapView.types";
import {
  CARD_PAN_PADDING,
  DEFAULT_MARKER_SIZE,
  DEFAULT_TONE,
} from "./constants";
import { useMapContent } from "./useMapContent";

// Google turns a click anywhere in the marker into a gmp-click, from a
// listener on the marker element itself, so a React handler runs too late to
// stop it: the card needs a native listener between it and the marker.
function isolateFromMarker(node: HTMLDivElement | null): (() => void) | void {
  if (!node) {
    return;
  }

  const stop = (event: Event) => event.stopPropagation();

  node.addEventListener("click", stop);
  node.addEventListener("keydown", stop);

  return () => {
    node.removeEventListener("click", stop);
    node.removeEventListener("keydown", stop);
  };
}

function panIntoView(map: google.maps.Map, card: HTMLElement): void {
  const box = map.getDiv().getBoundingClientRect();
  const rect = card.getBoundingClientRect();
  const pad = CARD_PAN_PADDING;

  const dy = Math.min(0, rect.top - (box.top + pad));
  const dx =
    rect.left < box.left + pad
      ? rect.left - (box.left + pad)
      : Math.max(0, rect.right - (box.right - pad));

  if (dx !== 0 || dy !== 0) {
    map.panBy(dx, dy);
  }
}

// A teardrop in a 24 by 32 box, tip at the bottom center.
const PIN_PATH =
  "M12 1C5.9 1 1 5.9 1 12c0 7.6 9.3 17.4 10.3 18.4a1 1 0 0 0 1.4 0C13.7 29.4 23 19.6 23 12 23 5.9 18.1 1 12 1z";

export function MapMarker({
  position,
  title,
  tone = DEFAULT_TONE,
  icon,
  label,
  size = DEFAULT_MARKER_SIZE,
  open,
  defaultOpen = false,
  onOpenChange,
  onClick,
  className,
  children,
}: MapMarkerProps): JSX.Element {
  const { t } = useLingui();
  const map = useMap();
  const { lat, lng } = position;

  useMapContent(useMemo(() => [{ lat, lng }], [lat, lng]));

  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const hasCard = children !== undefined && children !== null;
  const isOpen = hasCard && (open ?? uncontrolledOpen);

  const setOpen = (next: boolean) => {
    if (open === undefined) {
      setUncontrolledOpen(next);
    }

    onOpenChange?.(next);
  };

  const clickable = hasCard || onClick !== undefined;

  // The card mounts once Google has attached the marker, and is placed a
  // frame later: only then can the map pan just enough to show it whole.
  const attachCard = (node: HTMLDivElement | null): (() => void) | void => {
    if (!node || !map) {
      return;
    }

    const release = isolateFromMarker(node);

    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() => panIntoView(map, node)),
    );

    return () => {
      cancelAnimationFrame(frame);
      release?.();
    };
  };

  return (
    <AdvancedMarker
      position={position}
      title={title}
      zIndex={isOpen ? 1 : undefined}
      onClick={
        clickable
          ? () => {
              if (hasCard) {
                setOpen(!isOpen);
              }

              onClick?.(position);
            }
          : undefined
      }
    >
      <span
        data-slot="map-marker"
        data-open={isOpen || undefined}
        className={clsx(styles.marker, className)}
      >
        <span
          className={styles.pin({ tone })}
          style={resolveSize(size, [], styles.markerSize).style}
        >
          <svg
            className={styles.shape}
            viewBox="0 0 24 32"
            aria-hidden="true"
            focusable="false"
          >
            <path d={PIN_PATH} className={styles.fill} />
          </svg>
          {(icon || label) && (
            <span className={styles.glyph} aria-hidden="true">
              {icon ? (
                <Icon name={icon} size="100%" className={styles.glyphIcon} />
              ) : (
                label
              )}
            </span>
          )}
        </span>

        {isOpen && (
          <div
            ref={attachCard}
            role="group"
            aria-label={title}
            className={styles.card}
            data-slot="map-marker-card"
          >
            <div className={styles.cardHeader}>
              {title && <p className={styles.cardTitle}>{title}</p>}
              <Button
                variant="ghost"
                size="sm"
                className={styles.cardClose}
                aria-label={t`Close`}
                leftSection={<Icon name="X" size="sm" />}
                onClick={() => setOpen(false)}
              />
            </div>
            {children}
          </div>
        )}
      </span>
    </AdvancedMarker>
  );
}
